import {
  CAPABILITY_CATALOG_V1,
  type CapabilityCatalog,
  type CapabilityGuardrail,
} from "@/app/access-presets/data_sources/capabilities-catalog";

import type {
  CompatibleRole,
  PresetContext,
} from "@/core/data_sources/models/APIPermissionProfilePreset";

// Doc §16.2 · role guardrails for capabilities.
// A capability is forbidden when EVERY role in compatible_roles forbids it.
type RoleGuard = (cap: CapabilityGuardrail) => boolean;

const FORBIDDEN_IF: Record<CompatibleRole, RoleGuard> = {
  viewer: (cap) =>
    !!cap.write || !!cap.approval || !!cap.manage || !!cap.submit,
  member: (cap) => !!cap.approval || (!!cap.manage && !!cap.masterScope),
  supervisor: () => false,
  owner: () => false,
};

export function computeForbiddenCapabilities(
  roles: readonly CompatibleRole[],
  catalog: CapabilityCatalog = CAPABILITY_CATALOG_V1,
): string[] {
  if (roles.length === 0) return [];
  const forbidden: string[] = [];
  Object.values(catalog).forEach((caps) => {
    caps.forEach((cap) => {
      if (roles.every((role) => FORBIDDEN_IF[role](cap))) {
        forbidden.push(cap.code);
      }
    });
  });
  return forbidden;
}

export function capabilityGroupCount(capCodes: readonly string[]): number {
  const groups = new Set<string>();
  Object.entries(CAPABILITY_CATALOG_V1).forEach(([groupName, caps]) => {
    if (caps.some((cap) => capCodes.includes(cap.code))) groups.add(groupName);
  });
  return groups.size;
}

// Vendor + Master cannot coexist in the same preset (vendor_cannot_access_master).
export function hasVendorMasterConflict(
  contexts: readonly PresetContext[],
): boolean {
  return contexts.includes("vendor") && contexts.includes("master");
}

// Master context requires an admin role (owner/supervisor) in compatible_roles.
export function masterRequiresAdminRole(
  contexts: readonly PresetContext[],
  roles: readonly CompatibleRole[],
): boolean {
  if (!contexts.includes("master")) return false;
  return roles.includes("member") || roles.includes("viewer");
}

// Owner-only capabilities (e.g. library.install) require owner in compatible_roles.
export function requiresOwnerRole(
  capabilities: readonly string[],
  roles: readonly CompatibleRole[],
  catalog: CapabilityCatalog = CAPABILITY_CATALOG_V1,
): boolean {
  const ownerOnly = Object.values(catalog)
    .flat()
    .filter((cap) => cap.ownerOnly)
    .map((cap) => cap.code);
  const usesOwnerOnly = capabilities.some((code) => ownerOnly.includes(code));
  return usesOwnerOnly && !roles.includes("owner");
}
